import { View, Text } from 'react-native'
import { CheckCircle2, Circle } from '@/components/ui/icons'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'

type InspectionReadinessItem = {
  key: string
  label: string
  complete: boolean
  detail?: string
}

interface InspectionReadinessCardProps {
  title: string
  items: InspectionReadinessItem[]
  testID?: string
}

export function InspectionReadinessCard({ title, items, testID }: InspectionReadinessCardProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const completed = items.filter((item) => item.complete).length
  const progress = items.length > 0 ? completed / items.length : 0

  return (
    <View
      testID={testID}
      style={{
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        gap: SPACING.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <Text
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: FONTS.bodyBold,
            fontSize: 15,
            color: colors.textPrimary,
          }}
          numberOfLines={1}
        >
          {title}
        </Text>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textAccent }}>
          {completed}/{items.length}
        </Text>
      </View>

      <View
        style={{
          height: 6,
          borderRadius: RADIUS.full,
          overflow: 'hidden',
          backgroundColor: colors.surfaceSubtle,
        }}
      >
        <View
          style={{
            width: `${Math.round(progress * 100)}%`,
            height: '100%',
            borderRadius: RADIUS.full,
            backgroundColor: progress === 1 ? colors.success : COLORS.coral.DEFAULT,
          }}
        />
      </View>

      <View style={{ gap: SPACING.xs }}>
        {items.map((item) => (
          <View
            key={item.key}
            style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}
          >
            {item.complete ? (
              <CheckCircle2 size={17} color={colors.success} strokeWidth={2} />
            ) : (
              <Circle size={17} color={colors.textMuted} strokeWidth={2} />
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  lineHeight: 18,
                  color: item.complete ? colors.textSecondary : colors.textPrimary,
                }}
              >
                {item.label}
              </Text>
              {item.detail ? (
                <Text
                  style={{
                    marginTop: 1,
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    lineHeight: 16,
                    color: colors.textMuted,
                  }}
                >
                  {item.detail}
                </Text>
              ) : null}
            </View>
          </View>
        ))}
      </View>
    </View>
  )
}
